import { sendGAEvent } from "@next/third-parties/google";
import { ProfileUserResponse, PublicUserResponse } from "@authory-packages/core";
import { AuthenticatedUser } from "../../types/user";
import { logError } from "../../logging/logSentryError";
import { toError } from "../../utils/toError";
import { useMock } from "../utils/useMock";

export type GoogleAnalyticsUser = AuthenticatedUser | ProfileUserResponse | PublicUserResponse;

/**
 * Checks if Google Analytics may run for a user.
 * @param user The user the page is tracked for
 * @param gaId The measurement id, defaults to the Authory one
 * @returns False in development, without an id or for god mode users
 */
export function canUseGoogleAnalytics(
    user: GoogleAnalyticsUser,
    gaId: string | undefined = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID
): gaId is string {
    return !useMock() && !!gaId && user?.isGodMode !== true;
}

/**
 * Sends an event to the Google Analytics loaded by GoogleAnalyticsScript.
 * @param user The user the event is tracked for
 * @param name The event name
 * @param params The event parameters
 */
function sendGoogleAnalyticsEvent(user: GoogleAnalyticsUser, name: string, params: Record<string, string>) {
    if (typeof window === "undefined" || !canUseGoogleAnalytics(user)) return;

    try {
        sendGAEvent("event", name, params);
    } catch (err) {
        logError(toError(err));
    }
}

/**
 * Sends a page view for the current url.
 * @param user The user the page view is tracked for
 */
export function trackGoogleAnalyticsPageView(user: GoogleAnalyticsUser) {
    sendGoogleAnalyticsEvent(user, "page_view", { page_path: window.location.pathname + window.location.search });
}

/**
 * Sends the login event.
 * @param user The user that is logged in
 */
export function trackGoogleAnalyticsLogin(user: GoogleAnalyticsUser) {
    sendGoogleAnalyticsEvent(user, "is_logged_in", { event_category: "login" });
}
